/**
 * Trailing analytics (R8.10, Phase 6, ADR-0002 addendum 2026-07-07) --
 * Mood/14d, Energy/14d (+ sleep), Favorite-moments/30d + a deterministic
 * template insight. Also the 7-day mood trend for the extended check-in card
 * (R8.5's "Last 7 days · mood" mini chart). All pure read-only aggregations
 * over already-existing event types (`checkin.mood_set`/`energy_set`/
 * `sleep_hours_set`/`favorite_moment_set`) -- new query surface, no new
 * domain math beyond averages/percentages/simple period comparison.
 *
 * <14 (or <7) days of history is a DEFINED, DOCUMENTED behavior (AC-T24):
 * `average`/`trendPercent` are `null`, never NaN or a false-precision
 * number computed from a partial window. `daysWithData` always reflects the
 * true count so the UI can render "based on N of 14 days" copy.
 */

import type {
  CompassEvent,
  FavoriteMomentsBreakdownView,
  IsoDate,
  LifeAreaTag,
  TrailingAnalyticsView,
  TrendWindowView,
} from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { addDaysToDate } from '../habits/week';

const TREND_WINDOW_DAYS = 14;
const MOOD_TREND_WINDOW_DAYS = 7;
const FAVORITE_MOMENTS_WINDOW_DAYS = 30;
/** Share threshold (of total selections) above which the insight template fires -- a defensible heuristic, not statistics (mirrors the forecast confidence formula's own "documented heuristic, not real stats" posture). */
const INSIGHT_SHARE_THRESHOLD_PERCENT = 40;

const LIFE_AREA_TAG_LABELS: Record<LifeAreaTag, string> = {
  health: 'Health',
  relationship: 'Relationships',
  craft: 'Craft',
  fun: 'Fun',
  contribution: 'Contribution',
};

/** Inclusive [start, end] date range, `windowDays` days ending at `end`. */
function windowRange(end: string, windowDays: number): { readonly start: string; readonly end: string } {
  return { start: addDaysToDate(end, -(windowDays - 1)), end };
}

/**
 * Trailing-window average + trend-vs-prior-period for one `checkin.*_set`-
 * shaped scalar field, generic over the value extractor so mood/energy/
 * sleep-hours share one implementation (mirrors `computeDailyFields`'s own
 * "one generalized pass over several similarly-shaped fields" precedent).
 * `trendPercent` compares the trailing window's average against the PRIOR
 * window of equal length immediately before it -- null when either window
 * has zero data points (never a comparison against nothing).
 */
function computeTrendWindow<T extends CompassEvent['type']>(
  events: readonly CompassEvent[],
  eventType: T,
  extractValue: (event: Extract<CompassEvent, { readonly type: T }>) => number,
  today: string,
  windowDays: number,
): TrendWindowView {
  // excludingReverted MUST run over the FULL log before narrowing by type --
  // see reflection.ts's identical fix for why the reverse order silently
  // drops the log_entry.reverted events themselves before they can be seen.
  const active = eventsOfTypes(excludingReverted(events), [eventType]);
  const byDate = new Map<string, number>();
  for (const event of active) {
    // Last (canonical order) value per day wins, mirroring every other
    // last-write-wins field in this codebase (checkin.mood_set etc. are
    // themselves already last-write-wins per day at the source).
    byDate.set(event.occurredOn, extractValue(event));
  }

  const current = windowRange(today, windowDays);
  const currentValues = valuesInRange(byDate, current.start, current.end);

  const priorEnd = addDaysToDate(current.start, -1);
  const prior = windowRange(priorEnd, windowDays);
  const priorValues = valuesInRange(byDate, prior.start, prior.end);

  const average = currentValues.length > 0 ? mean(currentValues) : null;
  const priorAverage = priorValues.length > 0 ? mean(priorValues) : null;

  const trendPercent =
    average !== null && priorAverage !== null && priorAverage !== 0
      ? Math.round(((average - priorAverage) / priorAverage) * 1000) / 10
      : null;

  return {
    average: average !== null ? Math.round(average * 100) / 100 : null,
    trendPercent,
    daysWithData: currentValues.length,
    windowDays,
  };
}

function valuesInRange(byDate: ReadonlyMap<string, number>, start: string, end: string): number[] {
  const values: number[] = [];
  for (const [date, value] of byDate) {
    if (date >= start && date <= end) values.push(value);
  }
  return values;
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** Mood/14d (AC-T24). */
export function computeMoodTrend14d(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): TrendWindowView {
  return computeTrendWindow(events, 'checkin.mood_set', (e) => e.mood, today, TREND_WINDOW_DAYS);
}

/** Energy/14d (AC-T24). */
export function computeEnergyTrend14d(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): TrendWindowView {
  return computeTrendWindow(events, 'checkin.energy_set', (e) => e.energy, today, TREND_WINDOW_DAYS);
}

/** Sleep/14d -- rides alongside the Energy/14d card (contract doc comment). */
export function computeSleepTrend14d(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): TrendWindowView {
  return computeTrendWindow(events, 'checkin.sleep_hours_set', (e) => e.hours, today, TREND_WINDOW_DAYS);
}

/**
 * "Last 7 days · mood" mini chart (R8.5) -- one point per day in the
 * trailing 7-day window, `null` for a day with no surviving `checkin.
 * mood_set` event (AC-T16: rendered as a gap/absence, never a fabricated
 * value). Ordered oldest-first (chart reading order).
 */
export interface MoodTrendPoint {
  readonly date: IsoDate;
  readonly mood: number | null;
}

export function computeMoodTrend7d(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): readonly MoodTrendPoint[] {
  const active = eventsOfTypes(excludingReverted(events), ['checkin.mood_set']);
  const byDate = new Map<string, number>();
  for (const event of active) {
    byDate.set(event.occurredOn, event.mood);
  }

  const { start } = windowRange(today, MOOD_TREND_WINDOW_DAYS);
  const points: MoodTrendPoint[] = [];
  for (let i = 0; i < MOOD_TREND_WINDOW_DAYS; i += 1) {
    const date = addDaysToDate(start, i) as IsoDate;
    points.push({ date, mood: byDate.get(date) ?? null });
  }
  return points;
}

/**
 * Favorite-moments/30d breakdown (AC-T25) -- percentage share per
 * `LifeAreaTag` across the trailing 30 days' surviving `checkin.
 * favorite_moment_set` events. A day with multiple tags selected
 * contributes to EACH tag's count once (contract doc comment: "share of tag
 * selections, not share of days"). `insight` is a deterministic template
 * string, same input -> same output always (never AI-generated, AC-T25).
 */
export function computeFavoriteMoments30d(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): FavoriteMomentsBreakdownView {
  const { start, end } = windowRange(today, FAVORITE_MOMENTS_WINDOW_DAYS);
  const active = eventsOfTypes(excludingReverted(events), ['checkin.favorite_moment_set']);

  // Last (canonical order) event per day wins -- a day's favorite-moment
  // selection is itself last-write-wins at the source (mirrors mood/energy).
  const byDate = new Map<string, readonly LifeAreaTag[]>();
  for (const event of active) {
    if (event.occurredOn < start || event.occurredOn > end) continue;
    byDate.set(event.occurredOn, event.tags);
  }

  const counts = new Map<LifeAreaTag, number>();
  let totalSelections = 0;
  for (const tags of byDate.values()) {
    for (const tag of tags) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
      totalSelections += 1;
    }
  }

  const ALL_TAGS: readonly LifeAreaTag[] = ['health', 'relationship', 'craft', 'fun', 'contribution'];
  const byTag = ALL_TAGS.map((tag) => {
    const count = counts.get(tag) ?? 0;
    const sharePercent =
      totalSelections > 0 ? Math.round((count / totalSelections) * 1000) / 10 : 0;
    return { tag, count, sharePercent };
  });

  let insight: string | null = null;
  let topShare = -1;
  let topTag: LifeAreaTag | null = null;
  for (const row of byTag) {
    if (row.sharePercent > topShare) {
      topShare = row.sharePercent;
      topTag = row.tag;
    }
  }
  if (totalSelections > 0 && topTag !== null && topShare >= INSIGHT_SHARE_THRESHOLD_PERCENT) {
    insight = `${LIFE_AREA_TAG_LABELS[topTag]} is your center of gravity.`;
  }

  return { byTag, totalSelections, insight };
}

/** All three R8.10 cards in one query (a pure function of `(event log, today)`). */
export function getTrailingAnalytics(
  events: readonly CompassEvent[],
  today: IsoDate | string,
): TrailingAnalyticsView {
  return {
    moodTrend14d: computeMoodTrend14d(events, today),
    energyTrend14d: computeEnergyTrend14d(events, today),
    sleepTrend14d: computeSleepTrend14d(events, today),
    favoriteMoments30d: computeFavoriteMoments30d(events, today),
  };
}
